import React from 'react';
import { Link } from 'react-router-dom';

import { appConfig } from '../../config/appConfig';
import { SEOHead } from '../SEO';
import dentalVisual from '../../images/Comp_1.gif';
import saasVisual from '../../images/dashboard.png';
import manufacturingVisual from '../../images/Comp_2.gif';
import hvacVisual from '../../images/Comp_3.gif';
import cleaningVisual from '../../images/one.gif';
import crossBorderVisual from '../../images/Comp_5.gif';

const industries = [
  {
    title: 'Dental Practice M&A Advisor (DSO Roll-Up)',
    tagline: 'Dental & DSO',
    description:
      'Sell-side guidance for dental founders navigating DSO and private equity roll-ups. We defend valuation multiples with payer mix analytics, hygiene productivity insights, and confidential processes.',
    highlights: [
      'Payer mix + hygiene uplift dashboards',
      'Investor-grade narratives for DSOs & sponsors',
      'Credentialing and compliance sprint kits',
    ],
    visual: {
      image: dentalVisual,
      alt: 'Dental founders reviewing a DSO readiness dashboard.',
      gradient: 'from-[#2A2E7F]/80 via-[#5C6CFF]/70 to-[#F59E0B]/60',
      caption: 'Chair utilization • Membership mix • Hygiene productivity',
    },
    to: '/dentalpractice-mna-advisor',
  },
  {
    title: 'SaaS & Software M&A Advisor',
    tagline: 'SaaS & Software',
    description:
      'Sophisticated positioning for ARR quality, retention, and GTM efficiency. We rebuild SaaS metrics and orchestrate strategic and financial buyers to secure premium software exits.',
    highlights: [
      'ARR quality rebuild with retention cohorts',
      'Board-ready GTM efficiency storytelling',
      'Strategic + sponsor sprint orchestration',
    ],
    visual: {
      image: saasVisual,
      alt: 'Software metrics dashboard showing retention and ARR curves.',
      gradient: 'from-[#0B192E]/85 via-[#2563EB]/70 to-[#38BDF8]/60',
      caption: 'ARR quality • Net retention • GTM efficiency',
    },
    to: '/saas-mna-advisor',
  },
  {
    title: 'Manufacturing & Precision Machining M&A',
    tagline: 'Industrial & Machining',
    description:
      'Industrial sell-side expertise highlighting certifications, tolerances, backlog, and utilization. We pair machining operators with strategic and financial acquirers who value operational excellence.',
    highlights: [
      'Certifications and tolerances at-a-glance',
      'Backlog visibility with normalized EBITDA',
      'Cross-border strategic and sponsor access',
    ],
    visual: {
      image: manufacturingVisual,
      alt: 'Precision machining floor with advanced equipment and operators.',
      gradient: 'from-[#0F172A]/85 via-[#1E3A8A]/70 to-[#10B981]/60',
      caption: 'Certifications • Backlog health • Utilization',
    },
    to: '/manufacturing-precision-machining-mna',
  },
  {
    title: 'HVAC & Home Services M&A Advisor',
    tagline: 'Home Services',
    description:
      'Comprehensive readiness for HVAC, plumbing, and home services leaders. We foreground membership revenue, dispatch efficiency, and technician productivity to maximize outcomes.',
    highlights: [
      'Membership and SLA revenue heatmaps',
      'Dispatch efficiency + CAC/LTV benchmarks',
      'PE platform and regional strategic outreach',
    ],
    visual: {
      image: hvacVisual,
      alt: 'Technician scheduling board for HVAC and home services teams.',
      gradient: 'from-[#0B192E]/85 via-[#1D4ED8]/70 to-[#F97316]/60',
      caption: 'Membership revenue • Dispatch efficiency • Tech utilization',
    },
    to: '/hvac-home-services-mna-advisor',
  },
  {
    title: 'Commercial Cleaning & Facilities Services M&A Advisor',
    tagline: 'Facilities Services',
    description:
      'Contract-driven advisory for janitorial and soft services platforms. We package backlog stability, workforce performance, and compliance proof so buyers underwrite premium outcomes.',
    highlights: [
      'Contract retention and escalation analytics',
      'Route density + labor productivity dashboards',
      'Compliance, safety, and ESG documentation kits',
    ],
    visual: {
      image: cleaningVisual,
      alt: 'Facilities services team reviewing a contract health dashboard.',
      gradient: 'from-[#0B192E]/80 via-[#1E40AF]/70 to-[#22D3EE]/60',
      caption: 'Contract renewal • Dispatch efficiency • Compliance stack',
    },
    to: '/commercial-cleaning-mna-advisor',
  },
  {
    title: 'Cross-Border M&A Advisory (US–Europe)',
    tagline: 'Cross-Border',
    description:
      'Transatlantic process leadership coordinating legal, tax, and currency workstreams. We create pricing tension by engaging both US and European buyers simultaneously.',
    highlights: [
      'FX, tax, and legal workstreams staged early',
      'Synchronized US & EU buyer orchestration',
      'Regulatory translation without friction',
    ],
    visual: {
      image: crossBorderVisual,
      alt: 'Global deal team collaborating on a transatlantic transaction.',
      gradient: 'from-[#0B1B2E]/85 via-[#1E3A8A]/70 to-[#9333EA]/60',
      caption: 'FX readiness • Dual diligence flows • Tax structuring',
    },
    to: '/cross-border-mna-us-europe',
  },
];

const heroStats = [
  {
    label: 'Coverage Pods',
    value: 'Principal-Led',
    description:
      'Every vertical is led by an operator-turned-dealmaker who knows the buyer landscape first-hand.',
  },
  {
    label: 'Valuation Narratives',
    value: 'Data-Rich',
    description:
      'We recast KPIs, retention curves, and QoE-ready evidence so buyers can underwrite premium outcomes.',
  },
  {
    label: 'Buyer Access',
    value: 'Global Grid',
    description:
      'Strategic and sponsor relationships across North America and Europe with calibrated messaging.',
  },
];

const capabilityPillars = [
  {
    title: 'Founder Readiness Labs',
    description:
      'Deep-dive prep sessions to script the investment thesis, tighten data rooms, and pre-empt diligence gaps before buyers appear.',
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="3.5" />
        <path
          strokeLinecap="round"
          d="M12 2.5v2.75M12 18.75V21.5M2.5 12h2.75M18.75 12H21.5M5.76 5.76l1.94 1.94M16.3 16.3l1.94 1.94M18.24 5.76l-1.94 1.94M7.7 16.3l-1.94 1.94"
        />
      </svg>
    ),
  },
  {
    title: 'Precision Buyer Targeting',
    description:
      'Programmatic outreach informed by proprietary buyer intelligence, aligning thesis fit, appetite, and timing for each mandate.',
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4.5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5V12l3 3" />
      </svg>
    ),
  },
  {
    title: 'Dealroom Instrumentation',
    description:
      'Live dashboards capture buyer behavior, diligence velocity, and competitive tension so founders always know the next move.',
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4 18.5l4.8-5.3L13 16.9l4.7-7 2.3 1.6M4 5.5h16"
        />
        <path d="M4 5.5v13a1 1 0 001 1h14a1 1 0 001-1v-9" />
      </svg>
    ),
  },
];

const industriesStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Industries We Serve',
  itemListElement: industries.map((industry, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: industry.title,
    description: industry.description,
    url: `${appConfig.site.baseUrl}${industry.to}`,
  })),
};

const servicesStructuredData = industries.map((industry) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: industry.title,
  serviceType: industry.tagline,
  description: industry.description,
  provider: {
    '@type': 'Organization',
    name: appConfig.site.organizationName,
    url: appConfig.site.baseUrl,
  },
  areaServed: ['US', 'Europe'],
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    url: `${appConfig.site.baseUrl}${industry.to}`,
  },
}));

export default function IndustriesLanding() {
  return (
    <div className="bg-white">
      <SEOHead
        title="Industries We Serve | AMIVA Equity Partners"
        description="AMIVA Equity Partners provides specialized sell-side advisory across dental, SaaS, precision machining, HVAC, and cross-border transactions. Explore our industry playbooks."
        keywords={[
          'industry M&A advisor',
          'sell my business',
          'sector-specific M&A advisory',
        ]}
        canonical="/industries"
        ogImage={appConfig.assets.defaultOgImage}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
        ]}
        structuredData={[industriesStructuredData, ...servicesStructuredData]}
      />

      <section className="relative bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-80 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.18),_transparent_55%)]"></div>
          <div className="absolute -top-16 -left-28 w-96 h-96 bg-amiva-primary/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-4rem] right-[-6rem] w-[32rem] h-[32rem] bg-amiva-accent/25 rounded-full blur-3xl"></div>
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0B192E] via-transparent to-transparent"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
            Industries We Serve
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
            Specialized M&amp;A Playbooks for Founders
          </h1>
          <p className="text-lg sm:text-xl text-gray-200 leading-relaxed mx-auto max-w-3xl">
            We build industry-specific sell-side strategies that convert nuanced KPIs into premium
            valuations. Explore the sectors where AMIVA Equity Partners delivers founder-first outcomes.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {heroStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-white/10 border border-white/10 rounded-2xl p-6 backdrop-blur-sm transition-all duration-300 hover:bg-white/15"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-gray-200/80 mb-3">
                  {stat.label}
                </p>
                <h3 className="text-2xl font-semibold text-white mb-3">{stat.value}</h3>
                <p className="text-sm text-gray-200 leading-relaxed">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-[#e9f2ff]"></div>
        <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-amiva-accent/10 via-transparent to-transparent pointer-events-none"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              The AMIVA Standard
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-6">
              Congruent sector coverage built on our founder-first policy
            </h2>
            <p className="text-lg text-gray-600 mt-4 leading-relaxed">
              Every engagement is governed by our commitment to clarity, diligence discipline, and
              transparent advisory. These pillars ensure the experience feels consistent—no matter
              the industry you bring to market.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilityPillars.map((pillar) => (
              <div
                key={pillar.title}
                className="bg-white/80 backdrop-blur-md border border-white/60 shadow-custom-lg rounded-2xl p-8 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-custom-xl"
              >
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-amiva-primary/10 text-amiva-primary mb-5">
                  {pillar.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{pillar.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-gray-100 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase text-gray-600">
              Sector Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-6">
              Choose the industry track that matches your next chapter
            </h2>
            <p className="text-lg text-gray-600 mt-4 leading-relaxed">
              Each playbook harnesses the nuances of its market—buyer psychographics, diligence
              expectations, and KPIs that command premium outcomes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry) => (
              <article
                key={industry.to}
                className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-custom-lg transition-all duration-500 group hover:-translate-y-1 hover:shadow-custom-xl"
              >
                <div className="relative h-52 overflow-hidden rounded-t-3xl">
                  <img
                    src={industry.visual.image}
                    alt={industry.visual.alt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${industry.visual.gradient} mix-blend-multiply`}
                  ></div>
                  <div className="absolute inset-0 flex flex-col justify-between p-6">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold uppercase tracking-[0.28em]">
                        {industry.tagline}
                      </span>
                      <span className="text-xs font-medium text-white/70 uppercase tracking-[0.28em]">
                        Playbook
                      </span>
                    </div>
                    <p className="text-sm text-white/80 leading-snug max-w-xs">
                      {industry.visual.caption}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col p-8 sm:p-10 h-full">
                  <h3 className="text-2xl font-semibold text-gray-900">{industry.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed mt-4">
                    {industry.description}
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {industry.highlights.map((item) => (
                      <div
                        key={item}
                        className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50/80 p-4"
                      >
                        <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-amiva-primary shadow-custom-sm">
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                          >
                            <path
                              d="M5 10.5l3.2 3.2L15 6.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span className="text-sm text-gray-600 leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={industry.to}
                    className="mt-8 inline-flex w-max items-center gap-2 text-amiva-primary font-semibold uppercase tracking-[0.2em] text-xs transition-colors duration-300 group-hover:text-amiva-accent"
                  >
                    <span>Explore the playbook</span>
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white p-10 sm:p-12">
            <div className="absolute inset-0 opacity-40">
              <div className="absolute -top-20 right-10 w-64 h-64 bg-amiva-accent/60 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 -left-16 w-72 h-72 bg-amiva-primary/40 rounded-full blur-3xl"></div>
            </div>
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold leading-snug">
                <span className="text-white">Schedule a sector readiness session with our principals</span>
              </h2>
              <p className="text-lg text-gray-200 mt-4 leading-relaxed max-w-3xl">
                In one collaborative workshop we calibrate buyer theses, diligence expectations,
                and market timing—so you leave knowing exactly how your industry narrative will land.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/valuation"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-amiva-primary font-semibold shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  Request valuation readout
                </Link>
                <Link
                  to="/process"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/50 text-white font-semibold hover:bg-white/10 transition-colors duration-300"
                >
                  See our process
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
